import IcelandairLogo from '../../assets/IcelandairNewLogo.png';
import { PdfDocument, PdfImage } from './PdfDocument';

/**
 * Converts an image URL to a JPEG via a canvas, so the writer only has to embed JPEGs.
 * Transparent areas become white, which is what they look like on the page anyway.
 */
export const loadImageAsJpeg = (src: string): Promise<PdfImage> => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const context = canvas.getContext('2d');
        if (context === null) {
            reject(new Error('Canvas is not available'));
            return;
        }
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(img, 0, 0);
        canvas.toBlob(async blob => {
            if (blob === null) {
                reject(new Error('Could not encode the image'));
                return;
            }
            resolve({ bytes: new Uint8Array(await blob.arrayBuffer()), width: canvas.width, height: canvas.height });
        }, 'image/jpeg', 0.95);
    };
    img.onerror = () => reject(new Error(`Could not load image ${src}`));
    img.src = src;
});

let logoPromise: Promise<PdfImage> | undefined;

export const getIcelandairLogo = (): Promise<PdfImage> => {
    if (logoPromise === undefined) {
        logoPromise = loadImageAsJpeg(IcelandairLogo).catch(err => {
            logoPromise = undefined;
            throw err;
        });
    }
    return logoPromise;
};

// The report layouts below share a header and fixed page chrome:
// page margins 40/40/60/40, the logo pinned to the top right, "Page X of Y" in the top
// left corner and a centred grey footer line on every page.

export const REPORT_MARGINS = { top: 40, right: 40, bottom: 60, left: 40 };

const LOGO_WIDTH = 120;

/** Draws the logo at the top right of the current page, overhanging the right margin slightly. */
export const drawReportLogo = (doc: PdfDocument, logo: PdfImage) => {
    doc.drawImage(logo, doc.pageWidth - doc.margins.right + 20 - LOGO_WIDTH, doc.margins.top - 5, LOGO_WIDTH);
};

export const drawReportPageChrome = (doc: PdfDocument, footer: string, fontSize: number) => {
    doc.decoratePages((pageNumber, totalPages) => {
        doc.drawText(`Page ${pageNumber} of ${totalPages}`, 20, 20, { size: fontSize, color: '#808080' });
        const footerTop = doc.pageHeight - 30 - doc.lineHeight({ size: fontSize });
        doc.drawText(footer, 0, footerTop, { size: fontSize, color: '#808080', align: 'center' }, doc.pageWidth);
    });
};
